import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../providers/SessionProvider';
import { comptesApi, Compte } from '../../api/clients.api';
import { ArrowLeft, ChevronDown, ChevronUp, Info, CheckCircle, Clock, AlertTriangle, ArrowRight, Wallet, CreditCard } from 'lucide-react';
import solideLogo from '../../assets/images/services/solide.webp';

// Helper for currency formatting
const formatCurrency = (amount: number, currency: string = 'HTG') => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount).replace(/,/g, ' ') + ' ' + currency;
};

const BousanmSolide = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { userData } = useSession();
    const [loading, setLoading] = useState(true);
    const [bsAccounts, setBsAccounts] = useState<Compte[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<Compte | null>(null);
    const [planStats, setPlanStats] = useState<any>(null);
    const [showAccountSelector, setShowAccountSelector] = useState(false);

    // Modal states
    const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
    const [insufficientDetails, setInsufficientDetails] = useState({ balance: 0, required: 0 });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmDetails, setConfirmDetails] = useState({ amount: 0, balance: 0, convertedAmount: 0 });
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    // Source account for payment (can be different from plan account for cross-currency)
    const [sourceAccount, setSourceAccount] = useState<Compte | null>(null);
    const [showSourceSelector, setShowSourceSelector] = useState(false);
    const [exchangeRate, setExchangeRate] = useState<number | null>(null);

    // Initialize accounts from session
    useEffect(() => {
        const initAccounts = async () => {
            setLoading(true);
            try {
                if (userData.accounts && userData.accounts.length > 0) {
                    // Filter BS accounts
                    const filtered = userData.accounts.filter(acc =>
                        acc.type_compte?.toUpperCase() === 'BS' || acc.type === 'BS'
                    );
                    setBsAccounts(filtered);

                    if (filtered.length > 0) {
                        const first = filtered[0];
                        setSelectedAccount(first);
                        await loadPlanStats(first.code);
                    }
                }
            } catch (error) {
                console.error("Error initializing BS accounts", error);
            } finally {
                setLoading(false);
            }
        };

        if (!userData.isLoading) {
            initAccounts();
        }
    }, [userData.accounts, userData.isLoading]);

    const loadPlanStats = async (code: string) => {
        try {
            const stats = await comptesApi.getPlanSolideStats(code);
            if (stats && !stats.error) {
                setPlanStats(stats);
            } else {
                setPlanStats(null);
            }
        } catch (error) {
            console.error("Error loading plan stats", error);
            setPlanStats(null);
        }
    };

    const handleAccountChange = async (account: Compte) => {
        setSelectedAccount(account);
        setShowAccountSelector(false);
        await loadPlanStats(account.code);
    };

    const handlePayClick = async () => {
        if (!selectedAccount || !planStats?.financier?.montant_hebdo) return;

        const amount = planStats.financier.montant_hebdo;
        const planDevise = planStats.compte?.devise || selectedAccount.devise;

        // Default source to selected account, user can change in modal
        if (!sourceAccount) {
            setSourceAccount(selectedAccount);
        }

        const currentSource = sourceAccount || selectedAccount;
        const balance = currentSource.solde_BS ?? currentSource.soldeBS ?? 0;
        const sourceDevise = currentSource.devise;

        // Check if conversion needed and fetch rate
        let rate: number | null = null;
        let converted = 0;
        if (planDevise !== sourceDevise) {
            try {
                const { configApi } = await import('../../api/config.api');
                const rateData = await configApi.getCurrentRate();
                rate = rateData?.taux || 150;
                setExchangeRate(rate);
                // Convert: If plan is USD and source is HTG, multiply
                if (planDevise === 'USD' && sourceDevise === 'HTG') {
                    converted = amount * rate;
                } else if (planDevise === 'HTG' && sourceDevise === 'USD') {
                    converted = amount / rate;
                }
            } catch (e) {
                console.error("Failed to fetch rate", e);
                rate = 150; // fallback
                setExchangeRate(rate);
                converted = amount * rate;
            }
        } else {
            setExchangeRate(null);
            converted = amount;
        }

        // Show confirmation modal with ability to change source
        setConfirmDetails({ amount, balance, convertedAmount: converted });
        setShowConfirmModal(true);
    };

    const handleSourceChange = async (account: Compte) => {
        setSourceAccount(account);
        setShowSourceSelector(false);

        const balance = account.solde_BS ?? account.soldeBS ?? 0;
        const planDevise = planStats?.compte?.devise || selectedAccount?.devise;
        const sourceDevise = account.devise;
        const amount = planStats?.financier?.montant_hebdo || 0;

        // Recalculate with rate if currencies differ
        let converted = 0;
        if (planDevise !== sourceDevise && exchangeRate) {
            if (planDevise === 'USD' && sourceDevise === 'HTG') {
                converted = amount * exchangeRate;
            } else if (planDevise === 'HTG' && sourceDevise === 'USD') {
                converted = amount / exchangeRate;
            }
        } else if (planDevise !== sourceDevise) {
            // Fetch rate if not already fetched
            try {
                const { configApi } = await import('../../api/config.api');
                const rateData = await configApi.getCurrentRate();
                const rate = rateData?.taux || 150;
                setExchangeRate(rate);
                if (planDevise === 'USD' && sourceDevise === 'HTG') {
                    converted = amount * rate;
                } else {
                    converted = amount / rate;
                }
            } catch (e) {
                setExchangeRate(150);
                converted = amount * 150;
            }
        } else {
            setExchangeRate(null);
            converted = amount;
        }

        setConfirmDetails(prev => ({ ...prev, balance, convertedAmount: converted }));
    };

    const executePayment = async () => {
        if (!planStats?.financier?.montant_hebdo) return;

        const paySource = sourceAccount || selectedAccount;
        if (!paySource) return;

        const amount = planStats.financier.montant_hebdo;
        const balance = paySource.solde_BS ?? paySource.soldeBS ?? 0;

        // Check balance before payment
        // Note: For cross-currency, backend will convert. Frontend shows raw comparison but backend handles conversion
        // For now we just check raw balance and let backend handle conversion logic
        if (balance <= 0) {
            setInsufficientDetails({ balance, required: amount });
            setShowInsufficientBalanceModal(true);
            setShowConfirmModal(false);
            return;
        }

        setShowConfirmModal(false);
        setIsPaying(true);

        try {
            const { transactionsApi } = await import('../../api/transactions.api');

            const response = await transactionsApi.payService({
                compte_bs_id: paySource.id,  // Source account for debit
                compte_bs_code: paySource.code,
                compte_sol_id: planStats.compte?.id,  // Plan account
                compte_id: planStats.compte?.id,
                montant: amount
            });

            if (response.success) {
                setShowSuccessModal(true);
                // Refresh the plan's stats
                await loadPlanStats(selectedAccount!.code);
            } else {
                console.error("Payment failed", response);
                setInsufficientDetails({ balance, required: amount });
                setShowInsufficientBalanceModal(true);
            }
        } catch (error: any) {
            console.error("Payment error", error);
            const errBalance = paySource.solde_BS ?? paySource.soldeBS ?? 0;
            setInsufficientDetails({ balance: errBalance, required: amount });
            setShowInsufficientBalanceModal(true);
        } finally {
            setIsPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mb-4"></div>
                <p className="text-slate-500 font-medium">{t('services:bousanmSolide.loading')}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            {/* Header with Gradient */}
            <div className="relative overflow-hidden bg-gradient-to-b from-blue-900/10 to-blue-800/5 pt-6 pb-8 px-4">
                <div className="relative z-10 max-w-md mx-auto flex flex-col items-center text-center">
                    <button
                        onClick={() => navigate(-1)}
                        className="absolute left-0 top-1 p-2 rounded-full bg-white/70 backdrop-blur-sm border border-blue-900/10 hover:bg-white transition-colors"
                    >
                        <ArrowLeft size={20} className="text-blue-900" />
                    </button>

                    <img src={solideLogo} alt={t('services:bousanmSolide.headerTitle')} className="w-16 h-16 rounded-full shadow-md mb-3" />
                    <h1 className="text-xl font-bold text-blue-900 font-poppins">{t('services:bousanmSolide.headerTitle')}</h1>
                    <p className="text-sm text-slate-500 mt-1 max-w-xs">
                        {t('services:bousanmSolide.headerSubtitle')}
                    </p>
                </div>
            </div>

            <div className="max-w-md mx-auto px-4 -mt-4 space-y-4">
                {/* Account Section */}
                {bsAccounts.length > 0 ? (
                    <div className="space-y-4">
                        {/* Account Selector */}
                        {bsAccounts.length > 1 && (
                            <div className="bg-white/80 backdrop-blur-md border border-white/60 rounded-2xl shadow-sm overflow-hidden">
                                <button
                                    onClick={() => setShowAccountSelector(!showAccountSelector)}
                                    className="w-full flex items-center justify-between p-4"
                                >
                                    <div className="text-left">
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">{t('services:bousanmSolide.accountSelector.label')}</p>
                                        <p className="text-sm font-semibold text-slate-900">
                                            {selectedAccount?.code} - {selectedAccount?.type_compte}
                                        </p>
                                    </div>
                                    {showAccountSelector ? <ChevronUp size={20} className="text-blue-900" /> : <ChevronDown size={20} className="text-blue-900" />}
                                </button>

                                {showAccountSelector && (
                                    <div className="border-t border-slate-100 bg-white/50">
                                        {bsAccounts.map(acc => (
                                            <button
                                                key={acc.code}
                                                onClick={() => handleAccountChange(acc)}
                                                className={`w-full text-left p-3 px-4 text-sm flex items-center justify-between ${selectedAccount?.code === acc.code ? 'bg-blue-50/50 text-blue-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                                            >
                                                <span>{acc.code} - {acc.type_compte}</span>
                                                {selectedAccount?.code === acc.code && <CheckCircle size={16} className="text-blue-900" />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Plan Stats Card */}
                        {planStats && planStats.progression ? (
                            <div className="bg-white/60 backdrop-blur-md border border-white/60 rounded-3xl p-5 shadow-sm">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-bold text-slate-900">{t('services:bousanmSolide.planCard.title')}</h3>
                                        <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                                            <span>{t('services:bousanmSolide.planCard.planLabel', { plan: planStats.compte?.plan || "N/A" })}</span>
                                            {planStats.financier?.montant_hebdo && (
                                                <span className="font-semibold text-green-600">
                                                    • {formatCurrency(planStats.financier.montant_hebdo, selectedAccount?.devise)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`px-2.5 py-1 rounded-full border text-[10px] font-medium ${planStats.situation_actuelle?.est_a_jour ? 'bg-green-100/50 border-green-200 text-green-700' : 'bg-red-100/50 border-red-200 text-red-700'}`}>
                                        {planStats.situation_actuelle?.est_a_jour ? t('services:bousanmSolide.planCard.upToDate') : t('services:bousanmSolide.planCard.late')}
                                    </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="mb-6">
                                    <div className="flex justify-between items-end mb-2">
                                        <div>
                                            <p className="text-[10px] font-medium text-slate-500 uppercase">{t('services:bousanmSolide.planCard.progression')}</p>
                                            <p className="text-[10px] text-slate-400">
                                                {t('services:bousanmSolide.planCard.weeksCount', { paid: planStats.progression?.semaines_payees || 0, total: planStats.progression?.total_semaines || 0 })}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-bold text-blue-900">
                                                {planStats.progression?.pourcentage_completion?.toFixed(1) || 0}%
                                            </span>
                                            <span className="text-[10px] text-slate-400 block -mt-1">{t('services:bousanmSolide.planCard.accomplished')}</span>
                                        </div>
                                    </div>
                                    <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-600 rounded-full transition-all duration-1000"
                                            style={{ width: `${Math.min(planStats.progression?.pourcentage_completion || 0, 100)}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Next Due Date */}
                                {planStats.situation_actuelle?.prochaine_echeance && (
                                    <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <Clock size={16} className="text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-slate-500 uppercase font-medium">{t('services:bousanmSolide.planCard.nextDueDate')}</p>
                                            <p className="text-sm font-semibold text-slate-900">
                                                {new Date(planStats.situation_actuelle.prochaine_echeance).toLocaleDateString(i18n.language, { day: '2-digit', month: 'long', year: 'numeric' })}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="bg-white/60 backdrop-blur-md border border-white/60 rounded-3xl p-6 text-center shadow-sm">
                                <Info size={32} className="text-orange-400 mx-auto mb-3" />
                                <h3 className="font-semibold text-slate-900 mb-1">{t('services:bousanmSolide.noPlan.title')}</h3>
                                <p className="text-sm text-slate-500 mb-4">
                                    {t('services:bousanmSolide.noPlan.message')}
                                </p>
                                <p className="text-xs text-slate-400">{t('services:bousanmSolide.noPlan.contact')}</p>
                            </div>
                        )}

                        {/* Pay Button */}
                        {planStats?.financier?.montant_hebdo && (
                            <button
                                onClick={handlePayClick}
                                disabled={isPaying}
                                className="w-full bg-blue-900 hover:bg-blue-800 disabled:bg-blue-900/50 text-white p-4 rounded-2xl font-semibold shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                            >
                                {isPaying ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                                ) : (
                                    <>
                                        <span>{t('services:bousanmSolide.payButton')}</span>
                                        <span>{formatCurrency(planStats.financier.montant_hebdo, selectedAccount?.devise)}</span>
                                        <ArrowRight size={18} className="opacity-80" />
                                    </>
                                )}
                            </button>
                        )}

                    </div>
                ) : (
                    <div className="bg-white/60 backdrop-blur-md border border-white/60 rounded-3xl p-8 text-center shadow-sm">
                        <Wallet size={40} className="text-slate-300 mx-auto mb-4" />
                        <h3 className="font-semibold text-slate-900 mb-2">{t('services:bousanmSolide.noBsAccount.title')}</h3>
                        <p className="text-sm text-slate-500">
                            {t('services:bousanmSolide.noBsAccount.message')}
                        </p>
                    </div>
                )}

                {/* Info Card */}
                <div className="bg-white/60 backdrop-blur-md border border-white/60 rounded-2xl p-5 shadow-sm space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Info size={18} className="text-blue-900" />
                        <h4 className="font-semibold text-slate-900">{t('services:bousanmSolide.description.title')}</h4>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                        {t('services:bousanmSolide.description.text')}
                    </p>
                    <div className="space-y-3 pt-2">
                        <div className="flex gap-3 items-start">
                            <CreditCard size={16} className="text-blue-900 mt-0.5 shrink-0" />
                            <p className="text-xs text-slate-500">
                                {t('services:bousanmSolide.description.insufficientBalanceWarning')}
                            </p>
                        </div>
                        <div className="flex gap-3 items-start">
                            <CheckCircle size={16} className="text-green-600 mt-0.5 shrink-0" />
                            <p className="text-xs text-slate-500">{t('services:bousanmSolide.description.securePayments')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Insufficient Balance Modal */}
            {showInsufficientBalanceModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                        onClick={() => setShowInsufficientBalanceModal(false)}
                    />
                    <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm shadow-xl animate-in fade-in zoom-in duration-300">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={24} className="text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-center text-slate-900 mb-2">{t('services:bousanmSolide.insufficientBalance.title')}</h3>
                        <p className="text-sm text-center text-slate-500 mb-6">
                            {t('services:bousanmSolide.insufficientBalance.message')}
                        </p>

                        <div className="bg-slate-50 rounded-xl p-4 space-y-3 mb-6">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-500">{t('services:bousanmSolide.insufficientBalance.currentBalance')}</span>
                                <span className="text-sm font-semibold text-red-600">
                                    {formatCurrency(insufficientDetails.balance, selectedAccount?.devise)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-500">{t('services:bousanmSolide.insufficientBalance.requiredAmount')}</span>
                                <span className="text-sm font-semibold text-slate-900">
                                    {formatCurrency(insufficientDetails.required, selectedAccount?.devise)}
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowInsufficientBalanceModal(false)}
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 rounded-xl transition-colors"
                        >
                            {t('services:bousanmSolide.insufficientBalance.close')}
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                        onClick={() => setShowConfirmModal(false)}
                    />
                    <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm shadow-xl animate-in fade-in zoom-in duration-300">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CreditCard size={24} className="text-blue-600" />
                        </div>
                        <h3 className="text-lg font-bold text-center text-slate-900 mb-2">{t('services:bousanmSolide.verification.title')}</h3>
                        <p className="text-sm text-center text-slate-500 mb-4">
                            {t('services:bousanmSolide.verification.infoMessage')}
                        </p>

                        {/* Source Account Selector */}
                        <div className="mb-4">
                            <label className="text-xs text-slate-500 uppercase font-medium block mb-2">
                                {t('services:bousanmSolide.verification.sourceAccountLabel')}
                            </label>
                            <div className="relative">
                                <button
                                    onClick={() => setShowSourceSelector(!showSourceSelector)}
                                    className="w-full bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl p-3 flex items-center justify-between transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <Wallet size={16} className="text-slate-500" />
                                        <span className="text-sm font-medium text-slate-900">
                                            {(sourceAccount || selectedAccount)?.code}
                                        </span>
                                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                            {(sourceAccount || selectedAccount)?.devise}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-green-600">
                                            {formatCurrency(
                                                (sourceAccount || selectedAccount)?.solde_BS ??
                                                (sourceAccount || selectedAccount)?.soldeBS ?? 0,
                                                (sourceAccount || selectedAccount)?.devise
                                            )}
                                        </span>
                                        <ChevronDown size={16} className={`text-slate-400 transition-transform ${showSourceSelector ? 'rotate-180' : ''}`} />
                                    </div>
                                </button>

                                {showSourceSelector && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden">
                                        {bsAccounts.map(acc => (
                                            <button
                                                key={acc.id}
                                                onClick={() => handleSourceChange(acc)}
                                                className={`w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors ${(sourceAccount || selectedAccount)?.id === acc.id ? 'bg-blue-50' : ''
                                                    }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium">{acc.code}</span>
                                                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                                                        {acc.devise}
                                                    </span>
                                                </div>
                                                <span className="text-sm font-semibold text-green-600">
                                                    {formatCurrency(acc.solde_BS ?? acc.soldeBS ?? 0, acc.devise)}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-4 space-y-3 mb-6">
                             <div className="flex justify-between items-center">
                                 <span className="text-sm text-slate-500">{t('services:bousanmSolide.verification.amount')}</span>
                                 <span className="text-sm font-semibold text-blue-900">
                                     {formatCurrency(confirmDetails.amount, selectedAccount?.devise)}
                                 </span>
                             </div>
                            {/* Show conversion info if currencies differ */}
                             {(sourceAccount || selectedAccount)?.devise !== selectedAccount?.devise && exchangeRate && (
                                <>
                                    <div className="flex justify-between items-center text-orange-600 bg-orange-50 -mx-4 px-4 py-2">
                                        <span className="text-xs font-medium">⚡ {t('services:bousanmSolide.verification.exchangeRate')}</span>
                                        <span className="text-xs font-bold">1 USD = {exchangeRate.toLocaleString()} HTG</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-slate-500">{t('services:bousanmSolide.verification.convertedAmount')}</span>
                                        <span className="text-sm font-semibold text-orange-600">
                                            ≈ {formatCurrency(confirmDetails.convertedAmount, (sourceAccount || selectedAccount)?.devise)}
                                        </span>
                                    </div>
                                </>
                            )}
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-500">{t('services:bousanmSolide.verification.sourceBalance')}</span>
                                <span className="text-sm font-semibold text-slate-900">
                                    {formatCurrency(confirmDetails.balance, (sourceAccount || selectedAccount)?.devise)}
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => { setShowConfirmModal(false); setShowSourceSelector(false); }}
                                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-3 rounded-xl transition-colors"
                            >
                                {t('services:bousanmSolide.verification.cancel')}
                            </button>
                            <button
                                onClick={executePayment}
                                className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-medium py-3 rounded-xl transition-colors"
                            >
                                {t('services:bousanmSolide.verification.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                        onClick={() => setShowSuccessModal(false)}
                    />
                    <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm shadow-xl animate-in fade-in zoom-in duration-300">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle size={24} className="text-green-600" />
                        </div>
                        <h3 className="text-lg font-bold text-center text-slate-900 mb-2">{t('services:bousanmSolide.verification.paymentSuccess')}</h3>
                        <p className="text-sm text-center text-slate-500 mb-6">
                            {t('services:bousanmSolide.verification.contributionSuccess')}
                        </p>
                        <button
                            onClick={() => setShowSuccessModal(false)}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-xl transition-colors"
                        >
                            {t('services:bousanmSolide.insufficientBalance.close')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BousanmSolide;
